/**
 * Pricing Page — muestra los tiers públicos.
 * El usuario puede ir directamente al checkout desde aquí.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Check, Star, Users, Building2, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPE_META = {
  free:    { label: 'Free',         icon: User,      color: 'text-zinc-400' },
  client:  { label: 'Cliente',      icon: User,      color: 'text-primary' },
  trainer: { label: 'Entrenador',   icon: Star,      color: 'text-amber-400' },
  gym:     { label: 'Gimnasio',     icon: Building2, color: 'text-blue-400' },
};

const SCOPE_ORDER = ['client', 'trainer', 'gym'];

const formatPeriod = (p) => p === 'month' ? '/mes' : p === 'year' ? '/año' : '';

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tRes, mRes] = await Promise.all([
          axios.get(`${API}/subscriptions/tiers`),
          user ? axios.get(`${API}/subscriptions/me`) : Promise.resolve({ data: null }),
        ]);
        setTiers(tRes.data.tiers || []);
        setMySub(mRes.data?.subscription);
      } catch (e) {
        console.error(e);
        toast.error('No se pudieron cargar los planes');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const handleSubscribe = async (tier) => {
    if (!user) {
      navigate(`/register?tier=${tier.key}`);
      return;
    }
    if (tier.amount <= 0) {
      toast.info('Ya tienes acceso gratuito');
      return;
    }
    setCheckingOut(tier.key);
    try {
      const origin = window.location.origin;
      const r = await axios.post(`${API}/subscriptions/checkout`, {
        tier_key: tier.key,
        origin_url: origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('No se obtuvo la URL de pago');
        setCheckingOut(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el checkout');
      setCheckingOut(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Agrupar por scope
  const tiersByScope = {};
  tiers.forEach(t => {
    if (t.scope === 'free') return; // El free no se muestra como "comprable"
    if (!tiersByScope[t.scope]) tiersByScope[t.scope] = [];
    tiersByScope[t.scope].push(t);
  });

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-3">Planes y precios</h1>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            Elige el plan que mejor se adapta a ti. Cancela cuando quieras, sin permanencia.
            14 días gratis en planes Pro y Familia · 30 días gratis para entrenadores.
          </p>
          {mySub?.tier_key && mySub.tier_key !== 'free' && (
            <div className="mt-4 inline-block">
              <Badge variant="secondary" data-testid="my-current-tier">
                Plan actual: {mySub.tier_name}
              </Badge>
            </div>
          )}
        </header>

        {SCOPE_ORDER.map(scope => {
          const list = tiersByScope[scope] || [];
          if (list.length === 0) return null;
          const meta = SCOPE_META[scope];
          const Icon = meta.icon;
          return (
            <section key={scope} className="mb-14" data-testid={`pricing-section-${scope}`}>
              <div className="flex items-center gap-2 mb-6">
                <Icon className={`h-6 w-6 ${meta.color}`} />
                <h2 className="text-2xl font-bold text-foreground">
                  {scope === 'gym' ? 'Para Gimnasios' : scope === 'trainer' ? 'Para Entrenadores' : 'Para Clientes'}
                </h2>
              </div>

              <div className={`grid gap-6 ${list.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                {list.map(tier => {
                  const isCurrent = mySub?.tier_key === tier.key;
                  const featured = tier.key === 'client_pro_month' || tier.key === 'trainer_pro';
                  return (
                    <Card
                      key={tier.key}
                      className={`relative ${featured ? 'border-primary border-2 shadow-lg shadow-primary/10' : ''}`}
                      data-testid={`tier-card-${tier.key}`}
                    >
                      {featured && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground">⭐ Más popular</Badge>
                        </div>
                      )}
                      <CardHeader className="pb-3">
                        <h3 className="text-xl font-bold text-foreground">{tier.name}</h3>
                        <p className="text-sm text-muted-foreground min-h-[40px]">{tier.description}</p>
                        <div className="flex items-baseline gap-1 mt-3">
                          <span className="text-4xl font-bold text-foreground">€{tier.amount.toFixed(0)}</span>
                          <span className="text-sm text-muted-foreground">{formatPeriod(tier.period)}</span>
                        </div>
                        {tier.trial_days > 0 && (
                          <p className="text-xs text-emerald-400 mt-1">{tier.trial_days} días gratis</p>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <ul className="space-y-2 text-sm" data-testid={`tier-limits-${tier.key}`}>
                          {Object.entries(tier.limits).map(([k, v]) => {
                            const text = formatLimit(k, v);
                            if (!text) return null;
                            return (
                              <li key={k} className="flex items-start gap-2 text-foreground/90">
                                <Check className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                                <span>{text}</span>
                              </li>
                            );
                          })}
                        </ul>
                        <Button
                          className="w-full"
                          variant={featured ? 'default' : 'outline'}
                          disabled={isCurrent || checkingOut === tier.key}
                          onClick={() => handleSubscribe(tier)}
                          data-testid={`btn-subscribe-${tier.key}`}
                        >
                          {checkingOut === tier.key ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirigiendo...</>
                          ) : isCurrent ? (
                            'Plan actual'
                          ) : (
                            'Suscribirme'
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
          );
        })}

        <section className="text-center py-10 border-t border-border">
          <h3 className="text-xl font-semibold text-foreground mb-2">¿Eres una cadena de gimnasios?</h3>
          <p className="text-muted-foreground mb-4">
            Tenemos un plan Enterprise a medida con integración API y CSM dedicado.
          </p>
          <Button variant="outline" onClick={() => window.location.href = 'mailto:hola@guardiansfit.com?subject=Enterprise'}>
            Contactar para Enterprise
          </Button>
        </section>
      </div>
    </div>
  );
};

// Formatea límites en bullets humanos
function formatLimit(key, value) {
  if (value === true) {
    const labels = {
      can_design_workout: 'Diseña tu Entreno incluido',
      can_chat_write: 'Chat completo (escribir + adjuntos)',
      stats_advanced: 'Estadísticas avanzadas',
      white_label: 'Marca propia / White label',
      multi_trainer: 'Multi-trainer (varios entrenadores)',
      api_access: 'API access',
      csm_dedicated: 'Customer Success Manager dedicado',
    };
    return labels[key] || key.replace(/_/g, ' ');
  }
  if (value === false) return null;
  if (value === -1) {
    const labels = {
      max_sessions_logged: 'Sesiones ilimitadas',
      max_clients: 'Clientes ilimitados',
    };
    return labels[key] || 'Sin límite';
  }
  // Valores numéricos
  const numLabels = {
    max_sessions_logged: (v) => `Hasta ${v} sesiones registradas`,
    family_seats: (v) => v > 0 ? `Hasta ${v} perfiles familiares` : null,
    max_clients: (v) => `Hasta ${v} clientes activos`,
    max_storage_gb: (v) => `${v} GB de almacenamiento de videos`,
    max_members: (v) => `Hasta ${v} socios`,
    max_trainers: (v) => `${v} entrenadores incluidos`,
  };
  const fn = numLabels[key];
  if (fn) return fn(value);
  // Fallback genérico - filtrar valores 0/false
  if (!value) return null;
  return `${key.replace(/_/g, ' ')}: ${value}`;
}

// Filtra renders nulos
function safeFormatLimit(key, value) {
  const r = formatLimit(key, value);
  return r;
}

export default PricingPage;
